import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Eye, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Sticky top banner shown when an admin/exec is impersonating another user.
 * Renders above <TopBar> and offsets page content so nothing is hidden.
 */
export function ImpersonationBanner() {
  const { user, isImpersonating, stopImpersonation } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating || !user) return null;
  const meta = user.impersonation || {};

  const targetLabel = user.name || user.email;
  const targetRole = user.role || "";
  const scope = user.client_name
    ? `Client: ${user.client_name}`
    : (user.business_unit_id ? `BU scope` : "Full user view");

  const handleExit = async () => {
    setExiting(true);
    try {
      await stopImpersonation();
    } finally {
      setExiting(false);
      // Full reload guarantees every module (Capital / Finance / etc.) refetches
      // with the admin's own scope rather than any cached impersonated data.
      window.location.href = "/";
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-slate-900 border-b border-amber-600 shadow-sm"
    >
      <div className="max-w-[1400px] mx-auto px-6 h-11 flex items-center gap-3 text-sm">
        <Eye className="w-4 h-4 shrink-0" />
        <div className="flex-1 min-w-0 truncate">
          <span className="font-semibold">Impersonating</span>
          {" "}
          <span data-testid="impersonation-target-name" className="font-medium">
            {targetLabel}
          </span>
          <span className="mx-2 opacity-60">·</span>
          <span className="uppercase text-[10px] tracking-wider px-1.5 py-0.5 rounded bg-amber-600/20 border border-amber-700/30">
            {targetRole || "user"}
          </span>
          <span className="mx-2 opacity-60">·</span>
          <span className="opacity-80">{scope}</span>
          <span className="mx-2 opacity-60">·</span>
          <span className="text-[11px] uppercase tracking-wider opacity-80">Read-only</span>
          {meta.actor_email && (
            <>
              <span className="mx-2 opacity-60">·</span>
              <span className="text-[11px] opacity-70">as {meta.actor_email}</span>
            </>
          )}
        </div>
        <Button
          data-testid="impersonation-exit-btn"
          onClick={handleExit}
          disabled={exiting}
          size="sm"
          className="h-8 bg-slate-900 text-white hover:bg-slate-800"
        >
          {exiting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <X className="w-3.5 h-3.5 mr-1.5" />}
          Exit impersonation
        </Button>
      </div>
    </div>
  );
}
